/**
 * `fn.tools.hybridXOffset` (fn_tools.js:1195-1267 on KA-x-user-skill) — the
 * shared X-Offset convention the Add Section dialog's "+ / −" column is built
 * on. Ported here rather than reused from `canvas3dGeometry`'s copy because
 * that one is a method on the canvas geometry class and takes a
 * `CanvasTunnelModel`, not the plain `IAhuBoardTunnel` the dialog has.
 *
 * The convention, unchanged from legacy: `x_offset` IS the tunnel's own
 * UPSTREAM edge's absolute X — the left edge for Left-to-Right, the right
 * edge for Right-to-Left. There is deliberately no sign flip between the two
 * directions (KA-x-axis-sign).
 */

export type XOffsetSign = 'plus' | 'minus';

/**
 * The three fields the span is built from. Deliberately structural rather than
 * `IAhuBoardTunnel`: the canvas carries its own plain row type (`CanvasTunnel`)
 * and flips airflow direction from there, and legacy read the same three
 * attributes off the one Backbone model both layers shared.
 */
export interface TunnelSpanFields {
  length?: number | string | null;
  x_offset?: number | string | null;
  airflow_direction_id?: number | string | null;
}

/**
 * `getTunnelPhysicalSpan` — {left, right} physical X span (inches) of an
 * EXISTING tunnel, read through its OWN airflow direction. Peers in one
 * row:level chain are not guaranteed to share a direction (a user can flip an
 * individual tunnel), so this must be computed per tunnel — sorting on raw
 * `x_offset` instead silently breaks as soon as a chain mixes directions.
 */
export function getTunnelPhysicalSpan(tunnel: TunnelSpanFields): {
  left: number;
  right: number;
} {
  const length = parseFloat(String(tunnel.length ?? 0)) || 0;
  const xOffset = parseFloat(String(tunnel.x_offset ?? 0)) || 0;
  const isRtl = Number(tunnel.airflow_direction_id) === 2;
  if (isRtl) return { left: xOffset - length, right: xOffset };
  return { left: xOffset, right: xOffset + length };
}

/**
 * `defaultOffsetForNewTunnel` — signed `x_offset` for a NEW tunnel of
 * `length` / `airflowDirectionId` whose physical span butts up against
 * `extensionPoint`. 'plus' extends the new span rightward from that point,
 * 'minus' leftward. Returns the new tunnel's upstream edge directly, so a
 * 'plus' default is never negative and a 'minus' default never positive.
 */
export function defaultOffsetForNewTunnel(
  length: number,
  airflowDirectionId: number,
  extensionPoint: number,
  sign: XOffsetSign
): number {
  const len = Number(length) || 0;
  let targetLeft: number;
  let targetRight: number;
  if (sign === 'plus') {
    targetLeft = extensionPoint;
    targetRight = targetLeft + len;
  } else {
    targetRight = extensionPoint;
    targetLeft = targetRight - len;
  }
  return Number(airflowDirectionId) === 2 ? targetRight : targetLeft;
}

/**
 * `applySign` — the X-Offset input holds an UNSIGNED magnitude; the sign is
 * carried separately by the column header's "+ / −" toggle, so the user can
 * never type a value that disagrees with it.
 */
export function applySign(magnitude: number, sign: XOffsetSign): number {
  const m = Math.abs(Number(magnitude) || 0);
  return sign === 'minus' ? -m : m;
}

/**
 * `model/tunnel.js toggle_airflow_direction` (:203-230 on KA-x-user-skill) —
 * the PATCH body for flipping ONE tunnel's airflow direction.
 *
 * `airflow_direction_id` alone is not enough on a hybrid AHU. `x_offset` is the
 * tunnel's UPSTREAM edge's absolute X — the LEFT edge for Left-to-Right, the
 * RIGHT edge for Right-to-Left, with no sign flip between the two
 * (KA-x-axis-sign, see `getTunnelPhysicalSpan`). So flipping the direction
 * changes WHICH edge `x_offset` names, and leaving the stored number alone
 * slides the tunnel by its own length: a 24"-long section at `x_offset: 24`
 * spans 24→48 as LTR and 0→24 as RTL, i.e. it lands on top of its upstream
 * neighbour. Legacy therefore reads the CURRENT physical span first and hands
 * the new direction whichever edge it now names.
 *
 * Non-hybrid AHUs treat `x_offset` as a raw physical nudge that airflow does
 * not affect, so `x_offset` is left out of the body entirely there.
 */
export function airflowFlipPatch(
  tunnel: TunnelSpanFields,
  isHybrid: boolean
): { airflow_direction_id: number; x_offset?: number } {
  // tunnel_airflow_directions has exactly the two CMS rows (ids 1/2, G36 unit
  // convention) — legacy picks "the one that isn't current".
  const next = Number(tunnel.airflow_direction_id) === 1 ? 2 : 1;
  if (!isHybrid) return { airflow_direction_id: next };
  const currentlyLtr = Number(tunnel.airflow_direction_id) !== 2;
  const span = getTunnelPhysicalSpan(tunnel);
  return { airflow_direction_id: next, x_offset: currentlyLtr ? span.right : span.left };
}
